import { Box, styled, useMediaQuery } from "@mui/material";
import { Button } from "./Button";
import { H1, P1 } from "./Typography";

const HeroWrapper = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  height: "800px",
  display: "flex",
  alignItems: "flex-start",
  overflow: "hidden",
  paddingTop: "75px",
  backgroundColor: "#000", // Fix white edges
  "@media (max-width: 600px)": {
    height: "812px",
  },
}));

const BlurredBackground = styled(Box)<{ bgImage: string }>(({ bgImage }) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url('${bgImage}')`,
  backgroundSize: "cover",
  backgroundPosition: "center top",
  backgroundRepeat: "no-repeat",
  filter: "blur(40px)",
  transform: "scale(1.15)",
  pointerEvents: "none",
  zIndex: 0,
  display: "none",
  "@media (min-width: 1441px)": {
    display: "block",
  },
  "@media (max-width: 600px)": {
    display: "block",
    transform: "scale(1.3)", // Increase scale for mobile to cover edges better
    maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
    WebkitMaskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
  },
}));

const BackgroundImage = styled(Box)<{ bgImage: string }>(({ bgImage }) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url('${bgImage}')`,
  backgroundSize: "cover",
  backgroundPosition: "center top",
  backgroundRepeat: "no-repeat",
  pointerEvents: "none",
  zIndex: 1,
  "@media (min-width: 1441px)": {
    inset: "auto",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    maxWidth: "1440px",
    height: "100%",
    maskImage:
      "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
    WebkitMaskImage:
      "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
  },


}));

const ShadowLayer = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "343px",
  background: "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,1))",
  mixBlendMode: "soft-light",
  opacity: 0.35,
  transform: "rotate(180deg)",
  pointerEvents: "none",
}));

const BottomBlock = styled(Box)(() => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "50px",
  background: "#ffffff",
  borderRadius: "999px 999px 0 0",
  zIndex: 5,
  "@media (max-width: 600px)": {
    height: "32px",
  },
}));

const HeroContainer = styled(Box)(() => ({
  position: "relative",
  zIndex: 4,
  width: "100%",
  maxWidth: "1440px",
  margin: "0 auto",
  padding: "72px 120px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  height: "100%",
  "@media (max-width: 600px)": {
    padding: "12px 12px 32px 12px",
    alignItems: "center",
    justifyContent: "flex-start",
    // marginTop: "60px"
  },
}));

const ContentWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  maxWidth: "600px",
  "@media (max-width: 600px)": {
    gap: "20px",
    alignItems: "center",
    textAlign: "center",
    maxWidth: "100%",
  },
}));

const MainTitle = styled(H1)(() => ({
  color: "var(--text-white)",
  "@media (max-width: 600px)": {
    fontSize: "32px",
    lineHeight: "normal",
  },
}));

const Subtitle = styled(P1)(() => ({
  color: "var(--text-secondary)",
}));

interface HeroSectionProps {
  backgroundImage?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

export const HeroSection = ({
  backgroundImage,
  title,
  subtitle,
  buttonText = "Попробовать бесплатно",
}: HeroSectionProps) => {
  const isMobile = useMediaQuery("(max-width: 600px)");

  // Auto-generate mobile image path: /path/image.webp -> /path/image-mobile.webp
  const mobileBackgroundImage = backgroundImage?.replace(/(\.[\w\d]+)$/, "-mobile$1");
  const currentBg = (isMobile && mobileBackgroundImage ? mobileBackgroundImage : backgroundImage) || "";

  return (
    <HeroWrapper>

      <BlurredBackground bgImage={currentBg} />
      <BackgroundImage bgImage={currentBg} />

      <ShadowLayer />
      <BottomBlock />

      <HeroContainer>
        <ContentWrapper>
          <MainTitle variant="h1">{title}</MainTitle>
          <Subtitle variant="body1">{subtitle}</Subtitle>
          <Box sx={{ marginTop: "16px", "@media (max-width: 600px)": { marginTop: "8px" } }}>
            <Button variant="white">{buttonText}</Button>
          </Box>
        </ContentWrapper>
      </HeroContainer>
    </HeroWrapper>
  );
};
